import { redirect, type RouteObject } from 'react-router';
import { AppLayout } from '@/app/layouts';
import { UI_TEXT } from '@/shared/languages';
import { ROUTES, settingsPath } from '@/shared/routing';
import { composeLoaders } from '../compose-loaders';
import type { NavigationGuards } from '../navigation.guard';

/**
 * 본체 화면 — 네비(대시보드·발전통계·수익분석·이상알림·설비정보·설정·고객센터)가 가리키는 화면들. AppLayout 공유
 * (화면 이동 시 네비 chrome 리마운트 방지). 미구현 메뉴가 생기면 `@/pages/coming-soon` placeholder 로 연결한다.
 *
 * 각 child 의 `handle:{title,isSubpage}` = 타이틀 SSOT(모바일 셸 헤더·데스크탑 페이지 헤더).
 * 타이틀은 nav 라벨(`UI_TEXT.NAV_*`)과 일치.
 *
 * **'<'(뒤로가기) 정책** — `isSubpage` 는 라우트마다 **직접 선언**한다(파생 금지). 경로 깊이·path variable 로는
 * 판정할 수 없기 때문 — `/stats/inverter/:tab?/:plantId` 는 `/stats/inverter` 의 하위 화면이 아니라 **같은 화면**
 * 이 발전소를 URL 에 실체화한 것이라 하위가 아니고(뒤로 갈 상위 화면이 없다), 반대로 `/plants/:plantId` 는 목록
 * → 상세로 **들어간** 것이라 하위다. 둘은 경로 모양이 같아 구분이 불가능하다. 화면 관계는 사람이 아는 사실이니
 * 여기서 true/false 로 못 박는다(하단 탭 구성 같은 UI 사정에 의존하지 않는다 — 예전 `isTabRootRoute` 파생 폐기).
 *
 * '<' 는 히스토리 back 이 **아니라 Up**(상위 화면)이다 — 목적지는 현재 pathname 의 마지막 세그먼트를 뗀 경로
 * (`AppHeader.parentPath`). 하위 화면이 전부 `상위경로/식별자` 꼴이라 이 규칙 하나로 떨어지고, 어디서 들어왔든
 * **항상 같은 곳**으로 올라간다(대시보드에서 들어온 발전소 상세와 알림에서 들어온 발전소 상세가 같은 곳으로).
 * 그래서 딥링크·푸시로 곧장 열려 앱 내 히스토리가 없어도 안전하다(히스토리 깊이 추적이 필요 없다).
 * 최상위 화면의 탈출은 하단 탭·사이드바(항상 마운트).
 *
 * TODO(routes-structure): 도메인이 더 늘면 자식을 도메인별 파일(`alerts.ts` 등)로 분리하되 AppLayout 은 공유한다.
 */
export function createAppRoute(guards: NavigationGuards): RouteObject {
  return {
    Component: AppLayout,
    // 본체 전 화면 공통 게이트 — 라우트마다 반복하지 않고 레이아웃 라우트 한 곳에서 판정한다. 네비게이션마다
    // 재실행되므로 콜드스타트·앱 내 이동 모두 검사된다(구앱 NavigationGuard 상시검사와 동형).
    //
    // ⚠️ 자식은 loader 를 갖지 않는다 — RR7 은 부모·자식 loader 를 **병렬** 실행해서, 부모가 redirect 해도
    // 자식 loader 의 fetch 는 이미 발사된 뒤다(미인증·미동의 상태의 API 누수 + 글로벌 401 오발). 자식에
    // loader 가 꼭 필요하면 그 자식에서 `composeLoaders(guards.requireAuth, guards.requireConsent, ...)`
    // 로 가드를 재선언한다. (ADR `auth-bootstrap-per-guard` — 인증 판정을 하는 가드가 곧 bootstrap 지점)
    loader: composeLoaders(guards.requireAuth, guards.requireConsent),
    children: [
      {
        // 루트 `/` → `/dashboard` redirect. pathless AppLayout 의 **index** 로 둔다 — `path: '/'` 는 `*`
        // splat(children 보유 시)에 매칭 우선순위를 빼앗겨 `/` 가 NotFound 로 샌다(RR matchRoutes 실측). loader
        // redirect 라 렌더 전 서버식 전환(플래시 없음) — 인증·동의 판정은 부모 loader 가 담당(병렬 실행이라
        // 부모 redirect 가 우선하지 않을 수 있으나, 목적지 `/dashboard` 에서 같은 부모 가드가 다시 돈다).
        index: true,
        loader: () => redirect(ROUTES.DASHBOARD),
      },
      {
        // 대시보드 전체 (하단 탭 루트). 기본 착지점 — 로그인 후·푸시 기본·루트 redirect 목적지.
        path: ROUTES.DASHBOARD,
        handle: { title: UI_TEXT.NAV_DASHBOARD, isSubpage: false },
        lazy: { Component: () => import('@/pages/dashboard').then((m) => m.DashboardPage) },
      },
      {
        // 대시보드 단일(발전소 스코프) — **같은 페이지 컴포넌트**를 path 만 달리해 매칭한다. 라우트를 쪼갠 유일한
        // 이유는 `isSubpage` 를 스코프별로 선언하기 위해서다(전체 목록에서 발전소를 골라 들어간 화면 = 뒤로가기
        // 대상). 선택 세그먼트 한 라우트(`/dashboard/:plantId?`)로는 handle 이 두 스코프에 공유돼 가를 수 없다.
        // 컴포넌트 참조가 동일해 전체↔단일 전환에도 리마운트되지 않는다. 탭 활성 판정은 prefix 매칭이라 무영향.
        path: ROUTES.DASHBOARD_SINGLE,
        handle: { title: UI_TEXT.NAV_DASHBOARD, isSubpage: true },
        lazy: { Component: () => import('@/pages/dashboard').then((m) => m.DashboardPage) },
      },
      {
        // 발전소 이상 알림 (마이그레이션 파일럿). 데이터 entity=event, 화면 도메인=alert. 하단 탭 루트.
        path: ROUTES.ALERTS,
        handle: { title: UI_TEXT.NAV_ALERTS, isSubpage: false },
        lazy: { Component: () => import('@/pages/alerts').then((m) => m.AlertsPage) },
      },
      {
        // 발전량 조회(전체 스코프). 하단 탭/메뉴 진입 최상위 → '<' 없음.
        // 조회조건(기간·인버터)은 URL 에 안 싣는다 — path 는 정체성(발전소)만 운반.
        path: ROUTES.STATS_GENERATION,
        handle: { title: UI_TEXT.NAV_STATS_GENERATION, isSubpage: false },
        lazy: { Component: () => import('@/pages/generation').then((m) => m.GenerationPage) },
      },
      {
        // 발전량 조회(단일 발전소). 전체에서 발전소를 골라 **들어간** 화면 → 하위('<' 대상). 컴포넌트는 전체와
        // 동일하고 갈리는 건 위계 선언뿐(대시보드 DASHBOARD/DASHBOARD_SINGLE 과 같은 구조).
        path: ROUTES.STATS_GENERATION_SCOPED,
        handle: { title: UI_TEXT.NAV_STATS_GENERATION, isSubpage: true },
        lazy: { Component: () => import('@/pages/generation').then((m) => m.GenerationPage) },
      },
      {
        // 발전통계 > 인버터 상세분석 (4탭). 발전소(정체성)=path segment(`/stats/inverter/:plantId`). base(무 plantId)=
        // firstPlant 폴백(페이지가 Navigate). 탭·필터는 query.
        path: ROUTES.STATS_INVERTER,
        handle: {
          title: UI_TEXT.NAV_STATS_INVERTER,
          isSubpage: false,
        },
        lazy: {
          Component: () => import('@/pages/inverter-analysis').then((m) => m.InverterAnalysisPage),
        },
      },
      {
        // 드릴인(탭 + 발전소) — 경로는 깊지만 `/stats/inverter` 와 **같은 화면**(발전소를 URL 에 실체화)이라
        // 하위가 아니다 → '<' 없음. 탭 전환·발전소 변경도 이 라우트 안에서 일어난다.
        path: ROUTES.STATS_INVERTER_DETAIL,
        handle: {
          title: UI_TEXT.NAV_STATS_INVERTER,
          isSubpage: false,
        },
        lazy: {
          Component: () => import('@/pages/inverter-analysis').then((m) => m.InverterAnalysisPage),
        },
      },
      {
        // 수익분석(전체 스코프). 하단 탭/메뉴 진입 최상위 → '<' 없음.
        path: ROUTES.REVENUE,
        handle: { title: UI_TEXT.NAV_REVENUE, isSubpage: false },
        lazy: { Component: () => import('@/pages/revenue').then((m) => m.RevenuePage) },
      },
      {
        // 수익분석(단일 발전소). 전체에서 발전소를 골라 **들어간** 화면 → 하위('<' 대상).
        path: ROUTES.REVENUE_SCOPED,
        handle: { title: UI_TEXT.NAV_REVENUE, isSubpage: true },
        lazy: { Component: () => import('@/pages/revenue').then((m) => m.RevenuePage) },
      },
      {
        // 구 마이페이지(`/my`) → 설정 '내정보' 탭. 화면이 설정 탭으로 흡수됐지만 구앱 딥링크·푸시 url·북마크가
        // 남아 있어 라우트는 redirect shim 으로 존치한다(렌더 없음 → handle 불필요).
        path: ROUTES.MY,
        loader: () => redirect(settingsPath('my')),
      },
      {
        // 설비정보 (발전소 목록). 미완료(계약 미설정·운영준비중) 발전소를 상단 분리.
        // 네비 최상위 단일 메뉴 → '<' 없음. 이 목록에서 들어가는 상세가 하위 페이지(아래).
        path: ROUTES.PLANTS,
        handle: { title: UI_TEXT.NAV_INFO, isSubpage: false },
        lazy: { Component: () => import('@/pages/plants').then((m) => m.PlantInfoListPage) },
      },
      {
        // 발전소 정보 상세 (기본/설치/계약/구독 4섹션). 식별자(plantId)=path 맨 뒤. 타이틀은 페이지가 동적으로
        // 발전소 이름 오버라이드(useSetHeaderTitle). 목록에서 **들어간** 화면 → 하위('<' 대상).
        path: ROUTES.PLANT_INFO_DETAIL,
        handle: {
          title: UI_TEXT.NAV_INFO,
          isSubpage: true,
        },
        lazy: { Component: () => import('@/pages/plants').then((m) => m.PlantInfoDetailPage) },
      },
      {
        // 인버터 구성 (정보 상세 설치정보의 [자세히]에서 진입). 구 인버터 상세분석 '구성' 탭 — 인버터 제원은
        // 조회조건이 걸리는 분석이 아니라 설비 정보라 설비정보 위계로 옮겼다. 타이틀은 페이지가
        // `인버터 구성 · {발전소명}` 으로 동적 오버라이드. 정보 상세에서 **또 들어간** 2차 하위 → '<' 대상
        // (마지막 세그먼트를 떼면 `/plants/:plantId` — 어디서 들어왔든 정보 상세로 올라간다).
        path: ROUTES.PLANT_INVERTERS,
        handle: {
          title: UI_TEXT.NAV_INFO,
          isSubpage: true,
        },
        lazy: { Component: () => import('@/pages/plants').then((m) => m.PlantInvertersPage) },
      },
      {
        // 발전소 용도/계약 설정·변경 (정보 상세에서 진입, 주로 시트로 대체 — 이 라우트는 redirectUrl 흐름용 존치).
        // 저장 후 redirectUrl/뒤로가기 복귀. 정보 상세에서 **또 들어간** 2차 하위 → '<' 대상.
        path: ROUTES.PLANT_USE_SETUP,
        handle: {
          title: UI_TEXT.NAV_INFO,
          isSubpage: true,
        },
        lazy: {
          Component: () => import('@/pages/plant-use-setup').then((m) => m.PlantUseSetupPage),
        },
      },
      {
        // 고객센터 — 메뉴 진입 최상위 화면 → '<' 없음.
        // 채널톡 문의 + 약관·정책 목록(v3 consents) + 앱 버전.
        path: ROUTES.SUPPORT,
        handle: { title: UI_TEXT.NAV_SUPPORT, isSubpage: false },
        lazy: { Component: () => import('@/pages/support').then((m) => m.SupportPage) },
      },
      {
        // 설정 base — 탭 없이 들어오면 페이지가 기본 탭으로 materialize(`<Navigate replace>`). 라우트 loader
        // redirect 를 안 쓰는 이유는 기본 탭 목록을 페이지가 소유하기 때문(여기서 eager import 하면 코드분할이 깨진다).
        path: ROUTES.SETTINGS,
        handle: { title: UI_TEXT.NAV_SETTINGS, isSubpage: false },
        lazy: { Component: () => import('@/pages/settings').then((m) => m.SettingsPage) },
      },
      {
        // 설정 탭(내정보 / 환경설정) — 탭 전환은 **같은 화면** 안의 이동이라 하위가 아니다 → '<' 없음
        // (탈출은 탭바·메뉴). 인버터 상세분석의 탭 세그먼트와 같은 위계 규칙.
        path: ROUTES.SETTINGS_TAB,
        handle: { title: UI_TEXT.NAV_SETTINGS, isSubpage: false },
        lazy: { Component: () => import('@/pages/settings').then((m) => m.SettingsPage) },
      },
    ],
  };
}
